import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { IonSpinner, IonIcon } from "@ionic/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { lockClosedOutline, mailOutline, logoApple } from "ionicons/icons";
import { handleSignUp, handleSignIn, signInWithGoogle, signInWithApple, createOAuthUserDocument } from "../../lib/firebase/firebase";
import { storePendingFileId } from "../../lib/firebase/pendingFiles";
import { createCheckoutSession } from "../../lib/stripe/createCheckoutSession";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../../contexts/AuthContext";
import { getUTMForFirebase, getUTMForAnalytics } from "../../lib/analytics/utmTracking";
import { trackSignUp, trackInitiateCheckout, trackViewContent } from "../../lib/analytics/metaPixel";
import { isNativeiOS } from "../../lib/utils/platform";
import { hideIntercom, showIntercom } from "../../lib/intercom/intercom";
import IOSPaywall from "./IOSPaywall";
import ReactGA from "react-ga4";
import googleIcon from "../../assets/google.svg";
import logo from "../../assets/logo1.svg";
import laptopImage from "../../assets/laptopImage.png";
import branding from "../../config/branding";
import { isSvgTrace } from "../../config/appVariant";
import "./PaywallModal.css";
import "../../pages/Checkout/LifeTimePro.css";

/**
 * PaywallModal Component
 * 
 * Shown when a guest user tries to download/save/edit their SVG.
 * Prompts them to sign up or sign in to complete the action.
 * Also shown to authenticated users who don't have a premium subscription.
 */
const PaywallContent = ({
  isVisible,
  onClose,
  onSuccess,
  action, // 'download' | 'save' | 'editor' | 'signup'
  previewImage,
  pendingFileId,
  isAuthenticated = false,
  isPremium = false,
  previewOnly = false,
}) => {
  const [mode, setMode] = useState("signup"); // 'signup' | 'signin'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("yearly"); // For subscription modal
  const [showSubscriptionAfterSignIn, setShowSubscriptionAfterSignIn] = useState(false); // Show subscription modal after sign-in

  const { user, userIsPremium, userIsEmbroideryPremium, userHasAIAccess, refreshEmbroideryPremiumStatus, isLoading: authLoading } = useAuth();
  useEffect(() => {
    if (!isVisible) return;
    const dismiss = (event) => { if (event.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [isVisible, onClose]);

  const isLegacyUser = userIsPremium === "lifetime-pro";
  const isAIToolkit = action === 'ai-toolkit';

  useEffect(() => {
    if (isVisible) {
      if (!previewOnly) trackViewContent(action || "paywall");
      hideIntercom();
    } else {
      showIntercom();
    }
    return () => showIntercom();
  }, [isVisible, action, previewOnly]);

  // After sign-in, check if user has the required access for this action
  // For AI toolkit, only close if user has AI access (active subscriber)
  // For other actions, any premium tier is sufficient
  // On EmbroideryTrace, check embroidery membership specifically
  useEffect(() => {
    if (showSubscriptionAfterSignIn && user && !authLoading) {
      const hasRequiredAccess = isAIToolkit
        ? userHasAIAccess
        : (!isSvgTrace ? userIsEmbroideryPremium : userIsPremium);
      if (hasRequiredAccess) {
        console.log("✅ User has required access after sign-in, completing action");
        if (!isSvgTrace && refreshEmbroideryPremiumStatus) refreshEmbroideryPremiumStatus();
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
        setShowSubscriptionAfterSignIn(false);
      }
    }
  }, [showSubscriptionAfterSignIn, user, userIsPremium, userIsEmbroideryPremium, userHasAIAccess, authLoading, onSuccess, onClose, isAIToolkit, refreshEmbroideryPremiumStatus]);

  const shouldShowSubscription = 
    (isAuthenticated && !isPremium) || 
    (showSubscriptionAfterSignIn && user && !(isAIToolkit ? userHasAIAccess : (!isSvgTrace ? userIsEmbroideryPremium : userIsPremium)));

  useEffect(() => {
    if (isVisible && shouldShowSubscription && user && !previewOnly) {
      const functions = getFunctions();
      const trackView = httpsCallable(functions, "trackPaywallView");
      trackView().catch((err) => console.warn("Paywall view tracking failed:", err.message));
    }
  }, [isVisible, shouldShowSubscription, user, previewOnly]);

  const benefits = isAIToolkit ? (isLegacyUser ? [
    { icon: "🔄", text: "AI Retrace - Enhance low-res & blurry images" },
    { icon: "✨", text: "AI Modify - Edit images with text prompts" },
    { icon: "✂️", text: "Remove Background - Instant BG removal" },
    { icon: "🚀", text: "New AI features added regularly" },
  ] : [
    { icon: "🔄", text: "AI Retrace - Enhance low-res & blurry images" },
    { icon: "✨", text: "AI Modify - Edit images with text prompts" },
    { icon: "✂️", text: "Remove Background - Instant BG removal" },
    { icon: "📦", text: "Unlimited conversions + Commercial License" },
  ]) : isSvgTrace ? [
    { icon: "🧵", text: "Full Access to Embroidery Editor" },
    { icon: "✨", text: "Unlimited AI Conversions & SVG Editor" },
    { icon: "📦", text: "Bulk Convert + Commercial License" },
  ] : [
    { icon: "🧵", text: "Full Access to Embroidery Editor" },
    { icon: "✨", text: "Convert Images to Stitch Files" },
    { icon: "📦", text: "Export DST, PES, JEF & More" },
  ];

  const plans = isSvgTrace ? [
    {
      type: "yearly",
      price: "US$95.88",
      perMonth: "(US$7.99/month)",
      label: "Yearly",
      savings: "Best Value - Save $60",
      subscriptionType: "designerYearly",
    },
    {
      type: "monthly",
      price: "US$12.99",
      perMonth: "per month",
      label: "Monthly",
      subscriptionType: "designer",
    },
  ] : [
    {
      type: "yearly",
      price: "US$119.88",
      perMonth: "(US$9.99/month)",
      label: "Yearly",
      savings: "Best Value - Save 45%",
      subscriptionType: "embroideryYearly",
    },
    {
      type: "monthly",
      price: "US$17.99",
      perMonth: "per month",
      label: "Monthly",
      subscriptionType: "embroidery",
    },
  ];

  if (!isVisible) return null;

  // Show loading screen while waiting for auth to load after sign-in
  if (showSubscriptionAfterSignIn && (authLoading || !user)) {
    return (
      <div className="paywall-overlay">
        <div className="paywall-modal" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px" }}>
          <div style={{ textAlign: "center" }}>
            <IonSpinner name="crescent" style={{ width: "40px", height: "40px", color: "#F453C4" }} />
            <p style={{ marginTop: "16px", color: "#666", fontFamily: "Clash Grotesk" }}>Loading your account...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle checkout for authenticated but non-premium users
  // Matches the working implementation in LifeTimePro.jsx
  const handleCheckout = async (subscriptionType) => {
    if (!user) {
      setError("Please sign in first to subscribe.");
      return;
    }
    
    setLoading(true);
    setError("");
    trackInitiateCheckout(subscriptionType);
    
    try {
      // This will redirect to Stripe checkout
      await createCheckoutSession(user.uid, subscriptionType);
      // Note: The redirect happens inside createCheckoutSession via Stripe
      // Loading stays true until page navigates away
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setError("Failed to start checkout. Please try again.");
      setLoading(false);
    }
  };

  // Handle subscribe button click
  const handleSubscribe = () => {
    const plan = plans.find((p) => p.type === selectedPlan);
    if (plan) {
      handleCheckout(plan.subscriptionType);
    }
  };

  const FEATURE_CONFIG = {
    "ai-toolkit": {
      subtitle: "Enhance, modify & remove backgrounds in seconds — not hours",
      preview: { before: "😶‍🌫️ Blurry", after: "✨ Crystal clear" },
    },
    "generate": {
      subtitle: "Describe any image and get a ready-to-use SVG in seconds",
      preview: { before: '💬 "A red rose"', after: "🌹 SVG ready" },
    },
    "retrace": {
      subtitle: "Turn blurry, low-res images into sharp, detailed vectors",
      preview: { before: "😶‍🌫️ Low-res", after: "🔍 HD sharp" },
    },
    "modify": {
      subtitle: "Change colors, styles & elements just by describing what you want",
      preview: { before: '🎨 Original', after: '✏️ "Make it blue"' },
    },
    "default": {
      subtitle: "Join 50,000+ designers who convert images to SVG daily",
      preview: null,
    },
  };

  const featureConfig = FEATURE_CONFIG[action] ?? FEATURE_CONFIG["default"];

  if (shouldShowSubscription) {
    if (isNativeiOS()) {
      return (
        <IOSPaywall
          onClose={onClose}
          onSuccess={onSuccess}
          action={action}
        />
      );
    }

    if (!isSvgTrace) {
      return (
        <div className="paywall-overlay paywall-overlay--subscription" style={{ padding: 0 }}>
          <div className="checkout-split" onClick={(e) => e.stopPropagation()} style={{ animation: "none" }}>
            <div className="checkout-split__left">
              <div className="checkout-split__left-inner">
                <div className="checkout-split__logo">
                  <img src={branding.logo} alt={branding.appName} />
                </div>
                <img src={laptopImage} alt={branding.proName} className="checkout-split__laptop" />
              </div>
            </div>

            <div className="checkout-split__right">
              <button className="checkout-split__logout" onClick={onClose}>
                ✕ Close
              </button>

              <h2 className="checkout-split__title">
                Get Your File
              </h2>
              <p className="checkout-split__subtitle">
                Subscribe to export embroidery files. Cancel anytime.
              </p>

              <div className="checkout-split__benefits">
                {[
                  "Full Access to Embroidery Editor.",
                  "Convert Images to Stitch Files.",
                  "Auto Satin, Fill & Running Stitches.",
                  "Unlimited AI Enhanced Converts.",
                  "3,750 AI credits included every month.",
                  "Export DST, PES, JEF & More.",
                ].map((text) => (
                  <div className="checkout-split__benefit" key={text}>
                    <span className="checkout-split__benefit-check">✓</span>
                    <span className="checkout-split__benefit-text">{text}</span>
                  </div>
                ))}
              </div>

              {error && <div style={{ color: "#dc2626", textAlign: "center", marginBottom: "12px", fontSize: "14px", background: "rgba(220,38,38,0.06)", padding: "10px 14px", borderRadius: "10px", width: "100%" }}>{error}</div>}

              <div className="checkout-split__plans">
                {plans.map((plan) => (
                  <div
                    key={plan.type}
                    className={`checkout-split__plan ${selectedPlan === plan.type ? "checkout-split__plan--selected" : ""}`}
                    onClick={() => setSelectedPlan(plan.type)}
                  >
                    <div className="checkout-split__plan-left">
                      <div className={`checkout-split__plan-radio ${selectedPlan === plan.type ? "checkout-split__plan-radio--active" : ""}`}>
                        {selectedPlan === plan.type && <div className="checkout-split__plan-radio-dot" />}
                      </div>
                      <span className="checkout-split__plan-label">{plan.label}</span>
                      {plan.savings && (
                        <span className="checkout-split__plan-savings">{plan.savings}</span>
                      )}
                    </div>
                    <div className="checkout-split__plan-right">
                      <span className="checkout-split__plan-price">{plan.price}</span>
                      {plan.perMonth && (
                        <span className="checkout-split__plan-permonth">{plan.perMonth}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="checkout-split__cta"
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? (
                  <IonSpinner name="lines" />
                ) : (
                  <span>Subscribe now</span>
                )}
              </button>
              <p className="checkout-split__trial-note">
                Your selected plan is billed today and renews automatically. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="paywall-overlay paywall-overlay--subscription" style={{ padding: 0 }}>
        <div className="checkout-split" onClick={(e) => e.stopPropagation()} style={{ animation: "none" }}>
          <div className="checkout-split__left">
            <div className="checkout-split__left-inner">
              <div className="checkout-split__logo">
                <img src={branding.logo} alt={branding.appName} />
              </div>
              <img src={laptopImage} alt={branding.proName} className="checkout-split__laptop" />
            </div>
          </div>

          <div className="checkout-split__right">
            <button className="checkout-split__logout" onClick={onClose}>
              ✕ Close
            </button>

            <h2 className="checkout-split__title">
              {isLegacyUser ? `Upgrade to ${branding.proName}` : `Try ${branding.proName} Free for 5 Days`}
            </h2>
            <p className="checkout-split__subtitle">
              {isLegacyUser ? "Get instant access to all AI features." : "Start your free trial today. Cancel anytime during the trial period."}
            </p>

            <div className="checkout-split__benefits">
              {[
                "Full Access to SVG Editor.",
                "Full Access to Embroidery Editor.",
                "Unlimited AI Enhanced Converts.",
                "Commercial Use Image Generator.",
                "Bulk SVG Conversion.",
                "AI Image Resizer.",
              ].map((text) => (
                <div className="checkout-split__benefit" key={text}>
                  <span className="checkout-split__benefit-check">✓</span>
                  <span className="checkout-split__benefit-text">{text}</span>
                </div>
              ))}
            </div>

            {error && <div style={{ color: "#dc2626", textAlign: "center", marginBottom: "12px", fontSize: "14px", background: "rgba(220,38,38,0.06)", padding: "10px 14px", borderRadius: "10px", width: "100%" }}>{error}</div>}

            <div className="checkout-split__plans">
              {plans.map((plan) => (
                <div
                  key={plan.type}
                  className={`checkout-split__plan ${selectedPlan === plan.type ? "checkout-split__plan--selected" : ""}`}
                  onClick={() => setSelectedPlan(plan.type)}
                >
                  <div className="checkout-split__plan-left">
                    <div className={`checkout-split__plan-radio ${selectedPlan === plan.type ? "checkout-split__plan-radio--active" : ""}`}>
                      {selectedPlan === plan.type && <div className="checkout-split__plan-radio-dot" />}
                    </div>
                    <span className="checkout-split__plan-label">{plan.label}</span>
                    {plan.savings && (
                      <span className="checkout-split__plan-savings">{plan.savings}</span>
                    )}
                  </div>
                  <div className="checkout-split__plan-right">
                    <span className="checkout-split__plan-price">{plan.price}</span>
                    {plan.perMonth && (
                      <span className="checkout-split__plan-permonth">{plan.perMonth}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              className="checkout-split__cta"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? (
                <IonSpinner name="lines" />
              ) : (
                <span>{isLegacyUser ? "Upgrade Now" : "Try for Free"}</span>
              )}
            </button>
            <p className="checkout-split__trial-note">
              No charge for 5 days. Cancel anytime during your trial.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getReadableError = (errorCode) => {
    switch (errorCode) {
      case "auth/email-already-exists":
      case "auth/email-already-in-use":
        return "This email is already in use. Try signing in instead.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password must be at least 6 characters.";
      case "auth/user-not-found":
        return "No account found with this email. Please sign up.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-credential":
        return "Invalid email or password. Please try again.";
      default:
        return errorCode;
    }
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
    return regex.test(password);
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      if (mode === "signup") {
        handleEmailSignUp(event);
      } else {
        handleEmailSignIn(event);
      }
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password || !passwordCheck) {
      setError("Please fill in all fields");
      return;
    }

    if (!validatePassword(password)) {
      setError("Password must be at least 8 characters with uppercase, lowercase, number, and special character (!@#$%^&*)");
      return;
    }

    if (password !== passwordCheck) {
      setError("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      
      // Store pending file ID before signup
      if (pendingFileId) {
        storePendingFileId(pendingFileId);
      }

      const utmData = getUTMForFirebase();
      await handleSignUp(email, password, utmData);

      // Track signup event
      const utmAnalytics = getUTMForAnalytics();
      ReactGA.event("sign_up", {
        method: "email",
        source: "guest_converter",
        action: action,
        ...utmAnalytics,
      });
      trackSignUp("email");

      // New users are never premium - show subscription modal
      setShowSubscriptionAfterSignIn(true);
    } catch (error) {
      setError(getReadableError(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      
      // Store pending file ID before signin
      if (pendingFileId) {
        storePendingFileId(pendingFileId);
      }

      await handleSignIn(email, password);

      // Track signin event
      ReactGA.event("login", {
        method: "email",
        source: "guest_converter",
        action: action,
      });

      // Show subscription modal - auth context will determine if premium
      setShowSubscriptionAfterSignIn(true);
    } catch (error) {
      setError(getReadableError(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("🔐 PaywallModal: Starting Google sign-up...");

      // Store pending file ID before signup (important for redirect-based OAuth on mobile)
      if (pendingFileId) {
        storePendingFileId(pendingFileId);
        console.log("🔐 PaywallModal: Stored pending file ID:", pendingFileId);
      }

      // Also save converter state for recovery after redirect
      if (pendingFileId) {
        sessionStorage.setItem("paywallOAuthPending", JSON.stringify({
          pendingFileId,
          action,
          savedAt: Date.now(),
        }));
        console.log("🔐 PaywallModal: Saved OAuth pending state");
      }

      const result = await signInWithGoogle();
      
      // If result is null, we're doing a redirect (mobile) - don't proceed
      if (!result) {
        console.log("🔐 PaywallModal: Redirect flow initiated, waiting for redirect...");
        // Loading will stay true until redirect happens
        return;
      }

      console.log("🔐 PaywallModal: Popup completed successfully");
      const signedInUser = result.user;

      if (result.additionalUserInfo?.isNewUser) {
        const utmData = getUTMForFirebase();
        await createOAuthUserDocument(signedInUser.uid, signedInUser.email || "", utmData);
        console.log("🔐 PaywallModal: Created OAuth user document");

        ReactGA.event("sign_up", {
          method: "google",
          source: "guest_converter",
          action: action,
        });
        trackSignUp("google");
      }

      // Show subscription modal - auth context will determine if premium
      console.log("🔐 PaywallModal: Setting showSubscriptionAfterSignIn to true");
      setShowSubscriptionAfterSignIn(true);
    } catch (error) {
      console.error("🔐 PaywallModal: Google sign-up error:", error.code, error.message);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-up was cancelled. Please try again.");
      } else {
        setError("Failed to continue with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("🔐 PaywallModal: Starting Apple sign-up...");

      // Store pending file ID before signup (important for redirect-based OAuth on mobile)
      if (pendingFileId) {
        storePendingFileId(pendingFileId);
        console.log("🔐 PaywallModal: Stored pending file ID:", pendingFileId);
      }

      // Also save converter state for recovery after redirect
      if (pendingFileId) {
        sessionStorage.setItem("paywallOAuthPending", JSON.stringify({
          pendingFileId,
          action,
          savedAt: Date.now(),
        }));
        console.log("🔐 PaywallModal: Saved OAuth pending state");
      }

      const result = await signInWithApple();
      
      // If result is null, we're doing a redirect (mobile) - don't proceed
      if (!result) {
        console.log("🔐 PaywallModal: Redirect flow initiated, waiting for redirect...");
        // Loading will stay true until redirect happens
        return;
      }

      console.log("🔐 PaywallModal: Popup completed successfully");
      const signedInUser = result.user;

      if (result.additionalUserInfo?.isNewUser) {
        const utmData = getUTMForFirebase();
        await createOAuthUserDocument(signedInUser.uid, signedInUser.email || "", utmData);
        console.log("🔐 PaywallModal: Created OAuth user document");

        ReactGA.event("sign_up", {
          method: "apple",
          source: "guest_converter",
          action: action,
        });
        trackSignUp("apple");
      }

      // Show subscription modal - auth context will determine if premium
      console.log("🔐 PaywallModal: Setting showSubscriptionAfterSignIn to true");
      setShowSubscriptionAfterSignIn(true);
    } catch (error) {
      console.error("🔐 PaywallModal: Apple sign-up error:", error.code, error.message);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-up was cancelled. Please try again.");
      } else {
        setError("Failed to continue with Apple. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="paywall-overlay" onClick={onClose}>
      <div className="paywall-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="paywall-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {/* Logo */}
        <div className="paywall-logo">
          <img src={logo} alt={branding.appName} />
        </div>

        {/* Header */}
        <div className="paywall-header">
          <h2>
            {mode === "signup" 
              ? (isAIToolkit ? "Unlock AI Features" : "Export Your Design")
              : "Welcome Back!"
            }
          </h2>
          <p>
            {mode === "signup" 
              ? (isAIToolkit 
                  ? "Sign up to access powerful AI tools for your images."
                  : "Create a free account to download your stitch-ready embroidery files.")
              : "Sign in to continue to your account."
            }
          </p>
        </div>

        {/* Error message */}
        {error && <div className="paywall-error">{error}</div>}

        {/* Form */}
        <form className="paywall-form" onSubmit={mode === "signup" ? handleEmailSignUp : handleEmailSignIn}>
          <div className="paywall-input-group">
            <IonIcon icon={mailOutline} className="input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="paywall-input"
              disabled={loading}
            />
          </div>
          
          <div className="paywall-input-group">
            <IonIcon icon={lockClosedOutline} className="input-icon" />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="paywall-input"
              disabled={loading}
              onKeyDown={mode === "signin" ? handleKeyDown : undefined}
            />
          </div>
          
          {mode === "signup" && (
            <div className="paywall-input-group">
              <IonIcon icon={lockClosedOutline} className="input-icon" />
              <input
                type="password"
                placeholder="Verify Password"
                value={passwordCheck}
                onChange={(e) => setPasswordCheck(e.target.value)}
                className="paywall-input"
                disabled={loading}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}

          <button
            type="submit"
            className="paywall-submit"
            disabled={loading}
          >
            {loading ? (
              <IonSpinner name="lines" />
            ) : (
              mode === "signup" ? "Sign Up Free" : "Login"
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="paywall-divider">
          <span>OR</span>
        </div>

        {/* Social buttons */}
        <div className="paywall-social-buttons">
          <button
            className="social-button google-button"
            onClick={handleGoogleSignUp}
            disabled={loading}
          >
            <img src={googleIcon} alt="Google" className="social-icon" />
            Google
          </button>
          <button
            className="social-button apple-button"
            onClick={handleAppleSignUp}
            disabled={loading}
          >
            <IonIcon icon={logoApple} className="social-icon" />
            Apple
          </button>
        </div>

        {/* Toggle mode */}
        <p className="paywall-toggle">
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button onClick={() => { setMode("signin"); setError(""); }}>Sign In</button>
            </>
          ) : (
            <>
              Don't have an account?{" "}
              <button onClick={() => { setMode("signup"); setError(""); }}>Sign Up</button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

// Keep fixed overlays outside transformed/clipped editor ancestors.
const PaywallModal = (props) => createPortal(<PaywallContent {...props} />, document.body);

export default PaywallModal;
